/* eslint-disable @atlassian/relay/unused-fields */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { graphql, usePreloadedQuery, type EntryPointProps, type PreloadedQuery } from 'react-relay';
import type { AIEventBaseAttributes } from '@atlassian/ai-analytics';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ProactiveContainer } from '@atlassian/jira-ai-proactive-container/src/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type RelatedIssue,
	useRelatedIssuesActions,
} from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { RELATED_ISSUES_AI_EXPERIENCE } from '@atlassian/jira-atlassian-intelligence/src/common/ui/feedback-form/index.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { IssueLinkType } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import { ENTITY_FIELD_KEYS } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { useGlobalRef } from '@atlassian/jira-refs-store/src/index.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { transformAggResponseToIssueLinkTypes } from '@atlassian/jira-issue-agg-field-transformers/src/common/utils/issue-link-types-transformer.tsx';
import type { AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery } from './__generated__/AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery.graphql';
import {
	type RelatedIssuesSearchPayload,
	useFetchRelatedIssue,
} from './services/use-fetch-related-issues/index.tsx';
import {
	JiraIssueRelatedIssuesContainer,
	useRelatedIssues,
	type InvokedFrom,
} from './controllers/context.tsx';
import type { onIssueLinkSave } from './ui/types.tsx';
import { RelatedIssuesDraftList } from './ui/related-issues-draft-list/index.tsx';
import { Footer } from './ui/footer/index.tsx';
import messages from './messages.tsx';
import { getIssueLinkTypesWithRelatesTo, useIssueLinkTypesWithRelatesTo } from './utils/index.tsx';
import { RelatedIssuesDraftListIssueView } from './ui/related-issues-draft-list-issue-view/index.tsx';

const ISSUE_LINK_SEARCH_REF_KEY = 'issue-links-search.input';

const getAiEventAttributes = (invokedFrom?: InvokedFrom): AIEventBaseAttributes => ({
	aiFeatureName: 'jiraAIRelatedIssues',
	isAIFeature: 1,
	proactiveGeneratedAI:
		invokedFrom === 'issueLinksAddButton' ||
		invokedFrom === 'linkedWorkItemQuickAdd' ||
		invokedFrom === 'connectRelatedWorkLinkWorkItem' ||
		invokedFrom === 'globalIssueCreate'
			? 1
			: 0,
	userGeneratedAI: invokedFrom === 'improveIssueDropdown' ? 1 : 0,
});

const FEEDBACK_ENTRYPOINT_ID = 'f91a821a-c7b4-47a6-bc0c-b99c234f3124';
const FEEDBACK_COLLECTOR_ID = '043ab007-eb05-4694-9742-302b0a7f41a6';

type Props = {
	onIssueLinkSave: onIssueLinkSave;
	existingIssues: string[];
	customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload;
	invokedFromSource?: InvokedFrom;
	onCopyButtonClick?: (issueKey: string) => void;
	headerTitle?: MessageDescriptor;
	onSimilarIssueLinkGICButtonClick?: ((relatedIssue: RelatedIssue) => void) | null;
	issueKey: string;
	excludeWorkItemIds?: string[];
	summary?: string;
	description?: string;
};
type ComponentProps = Props & {
	aiRelatedIssuesIssueLinkTypesPreloadedQuery: PreloadedQuery<AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery>;
	isLoading?: boolean;
};

type Queries = {
	aiRelatedIssuesIssueLinkTypesPreloadedQuery: AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery;
};

const UNCATEGORISED_ERROR = 'UNKNOWN ERROR';

export const SuggestRelatedWorkItemsContentContainerOld = (props: Props) => {
	const {
		issueKey,
		existingIssues,
		onIssueLinkSave,
		invokedFromSource,
		onCopyButtonClick,
		onSimilarIssueLinkGICButtonClick,
		headerTitle = messages.linkSimilarWorkItems,
		customRelatedIssueSearchPayload,
	} = props;

	/**
	 * We track the current and previous issue key to support issue transitions,
	 * by resetting the state when the issue key changes, so as to not show the feature on issue view load
	 */
	const [currentIssueKey, setCurrentIssueKey] = useState(issueKey);
	const prevIssueKey = usePreviousWithInitial(issueKey);
	const [selectInputRef] = useGlobalRef(ISSUE_LINK_SEARCH_REF_KEY);

	const { setInvokedFrom } = useRelatedIssuesActions();

	if (invokedFromSource) {
		setInvokedFrom(invokedFromSource);
	}

	const singleInstrumentationId = useRef(uuid());
	const aiResultViewedSummaryLengthAnalyticsRef = useRef<string | undefined>();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { issueLinkTypes, relatesToIssueLinkType } = useIssueLinkTypesWithRelatesTo();

	const [
		{ invokedFrom, resourceSuggestions, currentStep },
		{
			discardAllSuggestions,
			setRelatedIssuesFetchStep,
			setRelatedIssuesDisplayStep,
			setRelatedIssuesLoadedStep,
			setResourceSuggestion,
			resetState,
		},
	] = useRelatedIssues();

	useEffect(() => {
		if (prevIssueKey !== issueKey) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setCurrentIssueKey(issueKey);
			resetState();
		}
	}, [currentIssueKey, issueKey, prevIssueKey, resetState]);

	const { data, error, fetchSuggestion } = useFetchRelatedIssue(existingIssues);

	useEffect(() => {
		if (data) {
			/**
			 * We want to move to local state instead of sweet state
			 * But, until Similar Issues Draft List is not platformised, we need to use sweet state
			 */
			data && setResourceSuggestion(data);
		}
	}, [data, setResourceSuggestion]);

	useEffect(() => {
		return () => {
			getUpdateAnalyticsFlowHelper().endSession(ENTITY_FIELD_KEYS.ISSUE_LINKS);
		};
	}, []);

	const draftSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) => suggestion.resourceStatus === 'DRAFT',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const shownSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) =>
				suggestion.resourceStatus === 'DRAFT' || suggestion.resourceStatus === 'SELECTED',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const selectedSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) => suggestion.resourceStatus === 'SELECTED',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const getAttributes = useCallback(
		() => ({
			...getAiEventAttributes(invokedFrom),
			singleInstrumentationId: singleInstrumentationId.current,
		}),
		[invokedFrom],
	);

	const { acceptAllSuggestions, acceptSelectedSuggestions } = useRelatedIssuesActions();

	const attributes = getAttributes();

	// Analytics callbacks
	const onAiInteractionInitiatedEvent = useCallback(() => {
		if (
			customRelatedIssueSearchPayload?._skipAiInteractionAnalytics &&
			fg('thor_gic_similar_issues_relay_migration')
		)
			return;

		fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
			invokedFrom,
			existingItemsCount: existingIssues.length,
			...attributes,
		});
	}, [
		createAnalyticsEvent,
		invokedFrom,
		existingIssues.length,
		attributes,
		customRelatedIssueSearchPayload?._skipAiInteractionAnalytics,
	]);

	// Base function for aiResult viewed events
	const onAiResultViewedEvent = useCallback(
		(doesNotMeetMAUCriteria: boolean, suggestionsExpanded: boolean, currentData?: typeof data) => {
			if (
				customRelatedIssueSearchPayload?._skipAiResultAnalytics &&
				fg('thor_gic_similar_issues_relay_migration')
			)
				return;

			if (!currentData) return;

			if (
				invokedFrom === 'globalIssueCreate' &&
				aiResultViewedSummaryLengthAnalyticsRef &&
				aiResultViewedSummaryLengthAnalyticsRef.current &&
				expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)
			) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					summaryLength: aiResultViewedSummaryLengthAnalyticsRef?.current?.length,
					invokedFrom,
					aiSuggestedCount: currentData.length,
					suggestionsExpanded,
					...attributes,
				});
			} else {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					aiSuggestedCount: currentData.length,
					suggestionsExpanded,
					...attributes,
				});
			}
		},
		[
			createAnalyticsEvent,
			attributes,
			invokedFrom,
			customRelatedIssueSearchPayload?._skipAiResultAnalytics,
		],
	);

	const onAiResultViewedEventWhenCollapsed = useCallback(() => {
		onAiResultViewedEvent(true, false, data);
	}, [onAiResultViewedEvent, data]);

	const onAiResultViewedEventWhenExpanded = useCallback(() => {
		onAiResultViewedEvent(false, true, data);

		getUpdateAnalyticsFlowHelper().fireAnalyticsStart(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
			analytics: createAnalyticsEvent({}),
			attributes: {
				fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
			},
		});
	}, [onAiResultViewedEvent, data, createAnalyticsEvent]);

	const onAiResultActionedEvent = useCallback(() => {
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'suggestionsHidden',
				aiItemsCount: data?.length,
				...attributes,
			},
		);
	}, [createAnalyticsEvent, data?.length, attributes]);

	const onAiResultErrorEvent = useCallback(
		(errorReceived: unknown) => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult error',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiErrorCode:
						errorReceived instanceof FetchError ? errorReceived.statusCode : UNCATEGORISED_ERROR,
					aiErrorMessage:
						errorReceived instanceof FetchError
							? errorReceived.message
							: JSON.stringify(errorReceived),
					...attributes,
				},
			);
		},
		[createAnalyticsEvent, attributes],
	);

	const onAiFeedbackSubmittedEvent = useCallback(
		(aiFeedbackResult: 'up' | 'down') => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiFeedback submitted',
				'aiRelatedIssuesSuggestionResultBox',
				{ aiFeedbackResult },
			);
		},
		[createAnalyticsEvent],
	);

	const onDisplaySuggestions = useCallback(() => {
		setRelatedIssuesDisplayStep();
	}, [setRelatedIssuesDisplayStep]);

	/**
	 * Refactor to use local state instead of sweet state in GRAVITYAI-2715 by passing back the data from ProactiveContainer
	 * and using that to set local state in the parent container here
	 */
	const onLoadedSuggestions = useCallback(() => {
		setRelatedIssuesLoadedStep();
	}, [setRelatedIssuesLoadedStep]);

	const onFetchSuggestion = useCallback(
		(searchPayload?: RelatedIssuesSearchPayload) => {
			fetchSuggestion(searchPayload);
			setRelatedIssuesFetchStep();
			if (expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)) {
				aiResultViewedSummaryLengthAnalyticsRef.current = searchPayload?.issue_summary;
			}
		},
		[fetchSuggestion, setRelatedIssuesFetchStep],
	);

	const [shouldReinitiate, setShouldReinitiate] = useState<number>(0);
	const setTriggerReinitiate = useCallback(() => {
		setShouldReinitiate(shouldReinitiate + 1);
	}, [shouldReinitiate]);

	const onAcceptAllSuggestions = useCallback(() => {
		if (!draftSuggestions) {
			return;
		}
		for (const suggestion of draftSuggestions) {
			const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${suggestion.resource.id}`;
			onIssueLinkSave(
				optimisticId,
				suggestion.resource,
				relatesToIssueLinkType,
				createAnalyticsEvent({}),
			);
		}
		acceptAllSuggestions();
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'bulkIssuesAccepted',
				aiItemsCount: draftSuggestions.length,
				...(fg('thor-additional-analytics-suggested-related-issue') && {
					linkedIssueId: draftSuggestions.map((suggestion) => suggestion.resource.id),
					...attributes,
				}),
			},
		);
	}, [
		acceptAllSuggestions,
		attributes,
		createAnalyticsEvent,
		draftSuggestions,
		onIssueLinkSave,
		relatesToIssueLinkType,
	]);

	const onAcceptSelectedSuggestions = useCallback(
		(issueLinkType: IssueLinkType) => {
			if (!selectedSuggestions) {
				return;
			}
			// Cycle through selected suggestions instead of draft suggestions
			for (const suggestion of selectedSuggestions) {
				const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${suggestion.resource.id}`;
				onIssueLinkSave(optimisticId, suggestion.resource, issueLinkType, createAnalyticsEvent({}));
				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
					analytics: createAnalyticsEvent({}),
					attributes: {
						fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
						actionTaken: 'add',
					},
				});
			}
			acceptSelectedSuggestions();
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiResultAction: 'bulkIssuesAccepted',
					aiItemsCount: selectedSuggestions.length,
					isRelatedToIssueLinkType: issueLinkType.id === relatesToIssueLinkType?.id,
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: selectedSuggestions.map((suggestion) => suggestion.resource.id),
						...attributes,
					}),
				},
			);

			if (fg('a11y-26912-connected-work-item-focus')) {
				if (selectInputRef) {
					selectInputRef?.focus();
				}
			}
		},
		[
			selectedSuggestions,
			acceptSelectedSuggestions,
			createAnalyticsEvent,
			relatesToIssueLinkType?.id,
			attributes,
			onIssueLinkSave,
			selectInputRef,
		],
	);

	const footerActionNode = useMemo(
		() => (
			<Footer
				// fg this (add draftSuggestions as default)
				suggestions={shownSuggestions}
				hasSelectedSuggestions={Boolean(selectedSuggestions && selectedSuggestions?.length > 0)}
				onAcceptAllSuggestions={onAcceptAllSuggestions}
				onAcceptSelectedSuggestions={onAcceptSelectedSuggestions}
				invokedFrom={invokedFrom}
				relatesToIssueLinkType={relatesToIssueLinkType}
				issueLinkTypes={issueLinkTypes}
			/>
		),
		[
			shownSuggestions,
			selectedSuggestions,
			onAcceptAllSuggestions,
			onAcceptSelectedSuggestions,
			invokedFrom,
			relatesToIssueLinkType,
			issueLinkTypes,
		],
	);

	const isBeta = useMemo(() => {
		if (invokedFrom === 'globalIssueCreate') {
			return true;
		}
		/**
		 * Similar Issues in issue view is GA
		 */
		return false;
	}, [invokedFrom]);

	/**
	 * We need a valid invokedFrom to show the feature, otherwise return null
	 */
	if (!invokedFrom) {
		return null;
	}

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<ProactiveContainer
				initialStep="FETCHING"
				data={shownSuggestions}
				error={error}
				loading={currentStep === 'FETCHING'}
				onFetchSuggestions={onFetchSuggestion}
				onDismissSuggestions={discardAllSuggestions}
				onDisplaySuggestions={onDisplaySuggestions}
				onLoadedSuggestions={onLoadedSuggestions}
				invokedFrom={invokedFrom}
				onAiInteractionInitiatedEvent={onAiInteractionInitiatedEvent}
				onAiResultErrorEvent={onAiResultErrorEvent}
				onAiResultViewedEventWhenCollapsed={onAiResultViewedEventWhenCollapsed}
				onAiResultViewedEventWhenExpanded={onAiResultViewedEventWhenExpanded}
				onAiResultActionedEvent={onAiResultActionedEvent}
				onAiFeedbackSubmittedEvent={onAiFeedbackSubmittedEvent}
				messages={{
					headerTitle,
					errorMessage: messages.similarIssuesCouldNotBeGeneratedIssueTermRefresh,
					noItemsMessage: messages.searchAgain,
					viewItemsMessage: messages.showNumResults,
					findingResultsMessage: messages.searchingForSimilarWorkItems,
				}}
				customRelatedIssueSearchPayload={props.customRelatedIssueSearchPayload}
				// Only show beta lozenge for GIC
				isBeta={isBeta}
				shouldReinitiate={shouldReinitiate}
				setTriggerReinitiate={setTriggerReinitiate}
				feedbackCollectorId={FEEDBACK_COLLECTOR_ID}
				feedbackFormEntrypointId={FEEDBACK_ENTRYPOINT_ID}
				aiExperience={RELATED_ISSUES_AI_EXPERIENCE}
				footerActionNode={footerActionNode}
			>
				{invokedFrom !== 'globalIssueCreate' ? (
					<RelatedIssuesDraftListIssueView
						onIssueLinkSave={onIssueLinkSave}
						invokedFrom={invokedFrom}
						onCopyButtonClick={onCopyButtonClick}
						onSimilarIssueLinkGICButtonClick={onSimilarIssueLinkGICButtonClick}
						// passing issueLinkTypes to avoid ts error and it is a no op because with FG off, it uses sweet state
						issueLinkTypes={issueLinkTypes}
					/>
				) : (
					<RelatedIssuesDraftList
						onCopyButtonClick={onCopyButtonClick}
						invokedFrom={invokedFrom}
						onIssueLinkSave={onIssueLinkSave}
						onSimilarIssueLinkGICButtonClick={onSimilarIssueLinkGICButtonClick}
						// passing issueLinkTypes to avoid ts error and it is a no op because with FG off, it uses sweet state
						issueLinkTypes={issueLinkTypes}
					/>
				)}
			</ProactiveContainer>
		</ContextualAnalyticsData>
	);
};

export const SuggestRelatedWorkItemsContentContainerNew = (props: ComponentProps) => {
	const {
		issueKey,
		existingIssues,
		onIssueLinkSave,
		invokedFromSource,
		onCopyButtonClick,
		onSimilarIssueLinkGICButtonClick,
		headerTitle = messages.linkSimilarWorkItems,
		aiRelatedIssuesIssueLinkTypesPreloadedQuery,
		isLoading,
		customRelatedIssueSearchPayload,
	} = props;

	const relayData = usePreloadedQuery<AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery>(
		graphql`
			query AiRelatedIssuesResourcesEntrypointIssueLinkTypesQuery($cloudId: ID!) @preloadable {
				jira {
					issueLinkTypes(cloudId: $cloudId) @optIn(to: "JiraIssueLinkTypes") {
						edges {
							node {
								linkTypeId
								inwards
								outwards
							}
						}
					}
				}
			}
		`,
		aiRelatedIssuesIssueLinkTypesPreloadedQuery,
	);
	const allIssueLinkTypes = transformAggResponseToIssueLinkTypes(relayData);

	/**
	 * We track the current and previous issue key to support issue transitions,
	 * by resetting the state when the issue key changes, so as to not show the feature on issue view load
	 */
	const [currentIssueKey, setCurrentIssueKey] = useState(issueKey);
	const prevIssueKey = usePreviousWithInitial(issueKey);
	const [selectInputRef] = useGlobalRef(ISSUE_LINK_SEARCH_REF_KEY);

	const { setInvokedFrom } = useRelatedIssuesActions();

	if (invokedFromSource) {
		setInvokedFrom(invokedFromSource);
	}

	const singleInstrumentationId = useRef(uuid());
	const aiResultViewedSummaryLengthAnalyticsRef = useRef<string | undefined>();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { issueLinkTypes, relatesToIssueLinkType } =
		getIssueLinkTypesWithRelatesTo(allIssueLinkTypes);

	const [
		{ invokedFrom, resourceSuggestions, currentStep },
		{
			discardAllSuggestions,
			setRelatedIssuesFetchStep,
			setRelatedIssuesDisplayStep,
			setRelatedIssuesLoadedStep,
			setResourceSuggestion,
			resetState,
		},
	] = useRelatedIssues();

	useEffect(() => {
		if (prevIssueKey !== issueKey) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setCurrentIssueKey(issueKey);
			resetState();
		}
	}, [currentIssueKey, issueKey, prevIssueKey, resetState]);

	const { data, error, fetchSuggestion } = useFetchRelatedIssue(existingIssues);

	useEffect(() => {
		if (data) {
			/**
			 * We want to move to local state instead of sweet state
			 * But, until Similar Issues Draft List is not platformised, we need to use sweet state
			 */
			data && setResourceSuggestion(data);
		}
	}, [data, setResourceSuggestion]);

	useEffect(() => {
		return () => {
			getUpdateAnalyticsFlowHelper().endSession(ENTITY_FIELD_KEYS.ISSUE_LINKS);
		};
	}, []);

	const draftSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) => suggestion.resourceStatus === 'DRAFT',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const shownSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) =>
				suggestion.resourceStatus === 'DRAFT' || suggestion.resourceStatus === 'SELECTED',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const selectedSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) => suggestion.resourceStatus === 'SELECTED',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const getAttributes = useCallback(
		() => ({
			...getAiEventAttributes(invokedFrom),
			singleInstrumentationId: singleInstrumentationId.current,
		}),
		[invokedFrom],
	);

	const { acceptAllSuggestions, acceptSelectedSuggestions } = useRelatedIssuesActions();

	const attributes = getAttributes();

	// Get the actual suggestions count - for GQL flow use aggData, for REST flow use data from hook
	const actualSuggestionsCount = useMemo(() => {
		if (
			invokedFrom === 'globalIssueCreate' &&
			props.customRelatedIssueSearchPayload?.isAggData &&
			props.customRelatedIssueSearchPayload._aggData &&
			fg('thor_gic_similar_issues_relay_migration')
		) {
			return props.customRelatedIssueSearchPayload._aggData.length;
		}

		return data?.length ?? 0;
	}, [invokedFrom, props.customRelatedIssueSearchPayload, data]);

	// Analytics callbacks
	const onAiInteractionInitiatedEvent = useCallback(() => {
		if (
			customRelatedIssueSearchPayload?._skipAiInteractionAnalytics &&
			fg('thor_gic_similar_issues_relay_migration')
		)
			return;

		fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
			invokedFrom,
			existingItemsCount: existingIssues.length,
			...attributes,
		});
	}, [
		createAnalyticsEvent,
		invokedFrom,
		existingIssues.length,
		attributes,
		customRelatedIssueSearchPayload?._skipAiInteractionAnalytics,
	]);

	// Base function for aiResult viewed events
	const onAiResultViewedEvent = useCallback(
		(doesNotMeetMAUCriteria: boolean, suggestionsExpanded: boolean, currentData?: typeof data) => {
			if (
				customRelatedIssueSearchPayload?._skipAiResultAnalytics &&
				fg('thor_gic_similar_issues_relay_migration')
			)
				return;

			if (!currentData && !fg('thor_gic_similar_issues_relay_migration')) {
				return;
			}

			if (
				invokedFrom === 'globalIssueCreate' &&
				aiResultViewedSummaryLengthAnalyticsRef &&
				aiResultViewedSummaryLengthAnalyticsRef.current &&
				expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)
			) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					summaryLength: aiResultViewedSummaryLengthAnalyticsRef?.current?.length,
					invokedFrom,
					aiSuggestedCount: fg('thor_gic_similar_issues_relay_migration')
						? currentData?.length
						: actualSuggestionsCount,
					suggestionsExpanded,
					...attributes,
				});
			} else {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					aiSuggestedCount: fg('thor_gic_similar_issues_relay_migration')
						? currentData?.length
						: actualSuggestionsCount,
					suggestionsExpanded,
					...attributes,
				});
			}
		},
		[
			createAnalyticsEvent,
			attributes,
			invokedFrom,
			actualSuggestionsCount,
			customRelatedIssueSearchPayload?._skipAiResultAnalytics,
		],
	);

	const onAiResultViewedEventWhenCollapsed = useCallback(() => {
		onAiResultViewedEvent(true, false, data);
	}, [onAiResultViewedEvent, data]);

	const onAiResultViewedEventWhenExpanded = useCallback(() => {
		onAiResultViewedEvent(false, true, data);

		getUpdateAnalyticsFlowHelper().fireAnalyticsStart(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
			analytics: createAnalyticsEvent({}),
			attributes: {
				fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
			},
		});
	}, [onAiResultViewedEvent, createAnalyticsEvent, data]);

	const onAiResultActionedEvent = useCallback(() => {
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'suggestionsHidden',
				aiItemsCount: fg('thor_gic_similar_issues_relay_migration')
					? data?.length
					: actualSuggestionsCount,
				...attributes,
			},
		);
	}, [createAnalyticsEvent, actualSuggestionsCount, attributes, data?.length]);

	const onAiResultErrorEvent = useCallback(
		(errorReceived: unknown) => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult error',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiErrorCode:
						errorReceived instanceof FetchError ? errorReceived.statusCode : UNCATEGORISED_ERROR,
					aiErrorMessage:
						errorReceived instanceof FetchError
							? errorReceived.message
							: JSON.stringify(errorReceived),
					...attributes,
				},
			);
		},
		[createAnalyticsEvent, attributes],
	);

	const onAiFeedbackSubmittedEvent = useCallback(
		(aiFeedbackResult: 'up' | 'down') => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiFeedback submitted',
				'aiRelatedIssuesSuggestionResultBox',
				{ aiFeedbackResult },
			);
		},
		[createAnalyticsEvent],
	);

	const onDisplaySuggestions = useCallback(() => {
		setRelatedIssuesDisplayStep();
	}, [setRelatedIssuesDisplayStep]);

	/**
	 * Refactor to use local state instead of sweet state in GRAVITYAI-2715 by passing back the data from ProactiveContainer
	 * and using that to set local state in the parent container here
	 */
	const onLoadedSuggestions = useCallback(() => {
		setRelatedIssuesLoadedStep();
	}, [setRelatedIssuesLoadedStep]);

	const onFetchSuggestion = useCallback(
		(searchPayload?: RelatedIssuesSearchPayload) => {
			fetchSuggestion(searchPayload);
			setRelatedIssuesFetchStep();
			if (expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)) {
				aiResultViewedSummaryLengthAnalyticsRef.current = searchPayload?.issue_summary;
			}
		},
		[fetchSuggestion, setRelatedIssuesFetchStep],
	);

	const [shouldReinitiate, setShouldReinitiate] = useState<number>(0);
	const setTriggerReinitiate = useCallback(() => {
		setShouldReinitiate(shouldReinitiate + 1);
	}, [shouldReinitiate]);

	const onAcceptAllSuggestions = useCallback(() => {
		if (!draftSuggestions) {
			return;
		}
		for (const suggestion of draftSuggestions) {
			const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${suggestion.resource.id}`;
			onIssueLinkSave(
				optimisticId,
				suggestion.resource,
				relatesToIssueLinkType,
				createAnalyticsEvent({}),
			);
		}
		acceptAllSuggestions();
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'bulkIssuesAccepted',
				aiItemsCount: draftSuggestions.length,
				...(fg('thor-additional-analytics-suggested-related-issue') && {
					linkedIssueId: draftSuggestions.map((suggestion) => suggestion.resource.id),
					...attributes,
				}),
			},
		);
	}, [
		acceptAllSuggestions,
		attributes,
		createAnalyticsEvent,
		draftSuggestions,
		onIssueLinkSave,
		relatesToIssueLinkType,
	]);

	const onAcceptSelectedSuggestions = useCallback(
		(issueLinkType: IssueLinkType) => {
			if (!selectedSuggestions) {
				return;
			}
			// Cycle through selected suggestions instead of draft suggestions
			for (const suggestion of selectedSuggestions) {
				const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${suggestion.resource.id}`;
				onIssueLinkSave(optimisticId, suggestion.resource, issueLinkType, createAnalyticsEvent({}));
				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
					analytics: createAnalyticsEvent({}),
					attributes: {
						fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
						actionTaken: 'add',
					},
				});
			}
			acceptSelectedSuggestions();
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiResultAction: 'bulkIssuesAccepted',
					aiItemsCount: selectedSuggestions.length,
					isRelatedToIssueLinkType: issueLinkType.id === relatesToIssueLinkType?.id,
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: selectedSuggestions.map((suggestion) => suggestion.resource.id),
						...attributes,
					}),
				},
			);

			if (fg('a11y-26912-connected-work-item-focus')) {
				if (selectInputRef) {
					selectInputRef?.focus();
				}
			}
		},
		[
			selectedSuggestions,
			acceptSelectedSuggestions,
			createAnalyticsEvent,
			relatesToIssueLinkType?.id,
			attributes,
			onIssueLinkSave,
			selectInputRef,
		],
	);

	const footerActionNode = useMemo(
		() => (
			<Footer
				// fg this (add draftSuggestions as default)
				suggestions={shownSuggestions}
				hasSelectedSuggestions={Boolean(selectedSuggestions && selectedSuggestions?.length > 0)}
				onAcceptAllSuggestions={onAcceptAllSuggestions}
				onAcceptSelectedSuggestions={onAcceptSelectedSuggestions}
				invokedFrom={invokedFrom}
				relatesToIssueLinkType={relatesToIssueLinkType}
				issueLinkTypes={issueLinkTypes}
			/>
		),
		[
			shownSuggestions,
			selectedSuggestions,
			onAcceptAllSuggestions,
			onAcceptSelectedSuggestions,
			invokedFrom,
			relatesToIssueLinkType,
			issueLinkTypes,
		],
	);

	const isBeta = useMemo(() => {
		if (invokedFrom === 'globalIssueCreate') {
			return true;
		}
		/**
		 * Similar Issues in issue view is GA
		 */
		return false;
	}, [invokedFrom]);

	/**
	 * We need a valid invokedFrom to show the feature, otherwise return null
	 */
	if (!invokedFrom) {
		return null;
	}

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<ProactiveContainer
				initialStep="FETCHING"
				data={shownSuggestions}
				error={error}
				loading={
					invokedFromSource === 'globalIssueCreate' && fg('thor_gic_similar_issues_relay_migration')
						? (isLoading ?? false)
						: currentStep === 'FETCHING'
				}
				onFetchSuggestions={onFetchSuggestion}
				onDismissSuggestions={discardAllSuggestions}
				onDisplaySuggestions={onDisplaySuggestions}
				onLoadedSuggestions={onLoadedSuggestions}
				invokedFrom={invokedFrom}
				onAiInteractionInitiatedEvent={onAiInteractionInitiatedEvent}
				onAiResultErrorEvent={onAiResultErrorEvent}
				onAiResultViewedEventWhenCollapsed={onAiResultViewedEventWhenCollapsed}
				onAiResultViewedEventWhenExpanded={onAiResultViewedEventWhenExpanded}
				onAiResultActionedEvent={onAiResultActionedEvent}
				onAiFeedbackSubmittedEvent={onAiFeedbackSubmittedEvent}
				messages={{
					headerTitle,
					errorMessage: messages.similarIssuesCouldNotBeGeneratedIssueTermRefresh,
					noItemsMessage: messages.searchAgain,
					viewItemsMessage: messages.showNumResults,
					findingResultsMessage: messages.searchingForSimilarWorkItems,
				}}
				customRelatedIssueSearchPayload={props.customRelatedIssueSearchPayload}
				// Only show beta lozenge for GIC
				isBeta={isBeta}
				shouldReinitiate={shouldReinitiate}
				setTriggerReinitiate={setTriggerReinitiate}
				feedbackCollectorId={FEEDBACK_COLLECTOR_ID}
				feedbackFormEntrypointId={FEEDBACK_ENTRYPOINT_ID}
				aiExperience={RELATED_ISSUES_AI_EXPERIENCE}
				footerActionNode={footerActionNode}
			>
				{invokedFrom !== 'globalIssueCreate' ? (
					<RelatedIssuesDraftListIssueView
						onIssueLinkSave={onIssueLinkSave}
						invokedFrom={invokedFrom}
						onCopyButtonClick={onCopyButtonClick}
						onSimilarIssueLinkGICButtonClick={onSimilarIssueLinkGICButtonClick}
						// passing issueLinkTypes to avoid ts error and it is a no op because with FG off, it uses sweet state
						issueLinkTypes={issueLinkTypes}
					/>
				) : (
					<RelatedIssuesDraftList
						onCopyButtonClick={onCopyButtonClick}
						invokedFrom={invokedFrom}
						onIssueLinkSave={onIssueLinkSave}
						onSimilarIssueLinkGICButtonClick={onSimilarIssueLinkGICButtonClick}
						// passing issueLinkTypes to avoid ts error and it is a no op because with FG off, it uses sweet state
						issueLinkTypes={issueLinkTypes}
					/>
				)}
			</ProactiveContainer>
		</ContextualAnalyticsData>
	);
};

export const SuggestRelatedWorkItemsContentContainer = componentWithCondition(
	() => fg('remove-issue-link-types-dfo'),
	SuggestRelatedWorkItemsContentContainerNew,
	SuggestRelatedWorkItemsContentContainerOld,
);

const AiRelatedIssuesResourcesEntrypoint = ({
	queries,
	props,
}: EntryPointProps<Queries, {}, Props, {}>): React.JSX.Element => {
	return (
		<JiraIssueRelatedIssuesContainer scope={`related-issues-${props.issueKey}`}>
			<SuggestRelatedWorkItemsContentContainer
				{...props}
				aiRelatedIssuesIssueLinkTypesPreloadedQuery={
					queries.aiRelatedIssuesIssueLinkTypesPreloadedQuery
				}
			/>
		</JiraIssueRelatedIssuesContainer>
	);
};

export default AiRelatedIssuesResourcesEntrypoint;
